import type { ElementType } from 'react';
import { clsx } from 'clsx';
import type { Polymorphic } from '../../polymorphism/polymorphic';
import inputStyles from '../input.module.scss';
import styles from './label.module.scss';

const DEFAULT_LABEL_ELEMENT = 'label';
type DefaultLabelElement = typeof DEFAULT_LABEL_ELEMENT;

interface BaseLabelProps {
  hasError?: boolean;
  isFixed?: boolean;
  isFloating?: boolean;
  isHidden?: boolean;
  isInteractive?: boolean;
  label: string;
  required?: boolean;
}

export type LabelProps<AsComponent extends ElementType = DefaultLabelElement> = Polymorphic.Props<AsComponent, BaseLabelProps>;

export function Label<AsComponent extends ElementType = DefaultLabelElement>({
  as,
  className,
  hasError,
  isFixed,
  isFloating,
  isHidden,
  isInteractive,
  label,
  required,
  ...rest
}: LabelProps<AsComponent>) {
  const Component = (as ?? DEFAULT_LABEL_ELEMENT) satisfies ElementType;

  return (
    <Component
      {...rest}
      className={clsx(
        inputStyles.input,
        styles.label,
        isFixed && styles.isFixed,
        hasError && styles.hasError,
        isHidden && styles.isHidden,
        isFloating && styles.isFloating,
        isInteractive && styles.isInteractive,
        className,
      )}
    >
      <span>
        {label}
        {required ? <span className={styles.requiredIndicator}>*</span> : null}
      </span>
    </Component>
  );
}
